import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  IconButton,
  Collapse,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CategoryIcon from "@mui/icons-material/Category";
import { motion } from "framer-motion";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import LayersIcon from "@mui/icons-material/Layers";
import AssignmentIcon from "@mui/icons-material/Assignment";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import EventNoteIcon from "@mui/icons-material/EventNote";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { getAllItems, StocksDashboardCounts } from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";

const cardColors = [
  "#9b59b6",
  "#3498db",
  "#7ed321",
  "#c4b5f6",
  "#2ecc71",
  "#e74c3c",
  "#f39c12",
  "#34495e",
  "#1abc9c",
  "#e67e22",
  "#ff6f61",
  "#8e44ad",
];

const Innerdashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [counts, setCounts] = useState({});
  const [stats, setStats] = useState([]);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);

  useEffect(() => {
    fetchData();
    setPage(1);
    fetchItems(1, false);
  }, []);

  const fetchItems = async (page = 1, append = false) => {
    const data = await getAllItems(page);

    if (append) {
      setRows((prev) => [...prev, ...data.results]);
    } else {
      setRows(data.results);
    }

    setHasMore(!!data.next);
  };

  const fetchData = async () => {
    const data = await StocksDashboardCounts();
    setCounts(data);

    setStats(
      [
        {
          label: "Total Category",
          value: data.categories,
          path: "/dashboard/category-list",
          icon: <CategoryIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: "#fff" }} />,
        },
        // {
        //   label: "Sub Category",
        //   value: data.subcategories,
        //   path: "/dashboard/subcategory-list",
        //   icon: <ViewModuleIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: "#fff" }} />,
        // },
        {
          label: "Unit",
          value: data.units,
          path: "/dashboard/unit",
          icon: <LayersIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: "#fff" }} />,
        },
        {
          label: "Items",
          value: data.items,
          path: "/dashboard/item-list",
          icon: <Inventory2Icon sx={{ fontSize: { xs: 20, sm: 30 }, color: "#fff" }} />,
        },
        // {
        //   label: "Indent",
        //   value: data.indents,
        //   path: "/dashboard/indent-list",
        //   icon: <AssignmentIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: "#fff" }} />,
        // },
        // {
        //   label: "Today Indent",
        //   value: data.today_indents,
        //   path: "/dashboard/indent-list",
        //   icon: <EventNoteIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: "#fff" }} />,
        // },
        {
          label: "Today Goods Out",
          value: data.today_goods_out,
          path: "/dashboard/issue-list",
          icon: <LocalShippingIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: "#fff" }} />,
        },
        // {
        //   label: "Transfers",
        //   value: data.transfers,
        //   path: "/dashboard/transferlist",
        //   icon: <SwapHorizIcon sx={{ fontSize: { xs: 20, sm: 30 }, color: "#fff" }} />,
        // },
      ].map((stat, i) => ({
        ...stat,
        bgColor: cardColors[i % cardColors.length],
      }))
    );
  };

  const navigate = useNavigate();

  const fetchMoreData = () => {
    if (hasMore) {
      fetchItems(page + 1, true);
      setPage((prev) => prev + 1);
    }
  };

  const filteredRows = rows.filter(
    (row) =>
      row.name.toLowerCase().includes(search.toLowerCase()) ||
      row.code.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    document.body.style.overflow = isMobile ? 'auto' : 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobile]);
  return (
    <Box sx={{ px: 2, pt: 1, pb: 6 }}>
      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 4, mt: -6 }}>
        {stats.map((stat, idx) => (
          <Grid item xs={6} sm={6} md={3} key={idx}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ scale: 1.05 }}
            >
              <Paper
                onClick={() => navigate(stat.path)}
                sx={{
                  p: { xs: 1.5, sm: 3 },
                  borderRadius: 0,
                  textAlign: "left",
                  backgroundColor: stat.bgColor,
                  height: { xs: 70, sm: 80 },
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: { xs: 1, sm: 2 },
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                <Box>{stat.icon}</Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, fontSize: { xs: 11, sm: 14 } }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#f1f1f1", fontSize: { xs: 10, sm: 12 } }}
                  >
                    {stat.value} units
                  </Typography>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ boxShadow: 3, mt: 2 }}>
        <CardHeader
          title="Items Alert"
          titleTypographyProps={{
            variant: "h6",
            fontWeight: "bold",
            color: "black",
          }}
          sx={{ pb: 0 }}
        />

        <CardContent>
          <Box id="items-table-print">
            <InfiniteScroll
              dataLength={filteredRows.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", p: 2 }}>
                  <Typography>Loading...</Typography>
                </Box>
              }
            // height={isMobile ? "60vh" : 480}
            >
              {isMobile ? (
                /* 📱 MOBILE VIEW - Dropdown Cards */
                <Box>
                  {filteredRows.map((row, index) => (
                    <Box key={index}>
                      {/* Header Card */}
                      <Box
                        onClick={() =>
                          setExpandedItem(
                            expandedItem === index ? null : index
                          )
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 1.5,
                          cursor: "pointer",
                          bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                          "&:active": { bgcolor: "#e3f2fd" },
                          borderLeft: "4px solid #3498db",
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.3,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: "#666",
                                minWidth: 25,
                              }}
                            >
                              #{index + 1}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.85rem",
                                flex: 1,
                              }}
                            >
                              {row.item_code}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: "#666",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              {row.name?.substring(0, 25) || "N/A"}
                              {row.name?.length > 25 ? "..." : ""}
                            </span>
                          </Typography>
                        </Box>

                        <IconButton size="small" sx={{ ml: 1 }}>
                          {expandedItem === index ? (
                            <ExpandLess fontSize="small" />
                          ) : (
                            <ExpandMore fontSize="small" />
                          )}
                        </IconButton>
                      </Box>

                      {/* Expanded Details */}
                      <Collapse
                        in={expandedItem === index}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box
                          sx={{
                            px: 2,
                            py: 1.5,
                            bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                            borderLeft: "4px solid #3498db",
                          }}
                        >
                          {/* Details Grid */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 1.2,
                            }}
                          >
                            {/* Item Code */}
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.65rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                  mb: 0.3,
                                }}
                              >
                                ITEM CODE
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  color: "#3498db",
                                }}
                              >
                                {row.item_code}
                              </Typography>
                            </Box>

                            {/* Stock Qty */}
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.65rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                  mb: 0.3,
                                }}
                              >
                                STOCK QTY
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  color: "#2ecc71",
                                }}
                              >
                                {row.current_stock}
                              </Typography>
                            </Box>

                            {/* Name - Full Width */}
                            <Box sx={{ gridColumn: "1 / -1" }}>
                              <Typography
                                sx={{
                                  fontSize: "0.65rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                  mb: 0.3,
                                }}
                              >
                                NAME
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  color: "#333",
                                }}
                              >
                                {row.name}
                              </Typography>
                            </Box>

                            {/* Min Qty */}
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.65rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                  mb: 0.3,
                                }}
                              >
                                MIN QTY
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  color: "#e74c3c",
                                }}
                              >
                                {row.min_quantity}
                              </Typography>
                            </Box>

                            {/* Category */}
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.65rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                  mb: 0.3,
                                }}
                              >
                                CATEGORY
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#555",
                                }}
                              >
                                {row.category_name}
                              </Typography>
                            </Box>

                            {/* Subcategory */}
                            {/* <Box sx={{ gridColumn: "1 / -1" }}>
                              <Typography
                                sx={{
                                  fontSize: "0.65rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                  mb: 0.3,
                                }}
                              >
                                SUBCATEGORY
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  color: "#555",
                                }}
                              >
                                {row.subcategory_name}
                              </Typography>
                            </Box> */}
                          </Box>
                        </Box>
                      </Collapse>
                      <Divider />
                    </Box>
                  ))}
                </Box>
              ) : (
                /* 💻 DESKTOP VIEW - Table (Unchanged) */
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: "53vh",
                    overflowX: "auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: 0,
                  }}
                >
                  <Table
                    size="small"
                    stickyHeader
                    sx={{
                      minWidth: 1000,
                      "& td, & th": {
                        textAlign: "center",
                        whiteSpace: "nowrap",
                        borderBottom: "1px solid #ccc",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: "#f5f5f5",
                          "& th": {
                            fontWeight: "bold",
                            color: "#333",
                            borderBottom: "1px solid #999",
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        <TableCell sx={{ padding: "0px" }}>S.No</TableCell>
                             <TableCell>Name</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Product Type</TableCell>
                             <TableCell>Unit</TableCell>
                        <TableCell>Stock Qty</TableCell>
                        <TableCell>Minimum Qty</TableCell>
                        <TableCell>Maximum Qty</TableCell>
                        {/* <TableCell>Subcategory</TableCell> */}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredRows.map((row, index) => (
                        <TableRow
                          hover
                          key={index}
                          sx={{
                            backgroundColor:
                              index % 2 === 0 ? "#fafafa" : "#ffffff",
                            "& td": {
                              paddingTop: "2px !important",
                              paddingBottom: "2px !important",
                              fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: 120,
                            },
                            "&:hover": {
                              backgroundColor: "#e3f2fd !important",
                            },
                          }}
                        >
                          <TableCell>{index + 1}</TableCell>
                          {/* <TableCell>{row.item_code}</TableCell> */}
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.category_name}</TableCell>
                          <TableCell>{row.size_name}</TableCell>
                          <TableCell>{row.unit_name}</TableCell>
                          <TableCell>{row.current_stock}</TableCell>
                          <TableCell>{row.min_quantity}</TableCell>
                          <TableCell>{row.max_quantity}</TableCell>
                          {/* <TableCell>{row.subcategory_name}</TableCell> */}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </InfiniteScroll>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Innerdashboard;